import { Service, inject } from '@angular/core'
import {
  AccountSnapshot,
  CatalogueItemInfo,
  CreateCheckoutBody,
  PlanTier,
  SmsCreditLedgerInfo,
} from '@seiri/shared/common'
import { ApiService } from '../api.service'

/** A purchase either redirects to Stripe, or was applied in place and returns fresh state. */
export type CheckoutResponse = { url: string } | { updated: true; snapshot: AccountSnapshot }

@Service()
export class ManageAccountApiService {
  private readonly api = inject(ApiService)

  private base(entityId: string): string {
    return `manage/entities/${entityId}/account`
  }

  getSnapshot(entityId: string) {
    return this.api.protectedFetchJson<AccountSnapshot>(this.base(entityId))
  }

  getCatalogue(entityId: string) {
    return this.api.protectedFetchJson<CatalogueItemInfo[]>(`${this.base(entityId)}/catalogue`)
  }

  checkout(entityId: string, body: CreateCheckoutBody) {
    return this.api.protectedFetchJson<CheckoutResponse>(`${this.base(entityId)}/checkout`, {
      body: JSON.stringify(body),
      method: 'POST',
    })
  }

  createStripePortalSession(entityId: string) {
    return this.api.protectedFetchJson<{ url: string }>(`${this.base(entityId)}/portal`, {
      method: 'POST',
    })
  }

  verifyCheckoutSession(entityId: string, sessionId: string) {
    const url = this.api.createURL(`${this.base(entityId)}/verify-session`)
    url.search = this.api.toQueryParams({ sessionId })
    return this.api.protectedFetchJson<{ activated: boolean; snapshot: AccountSnapshot }>(url, {
      method: 'POST',
    })
  }

  syncFromStripe(entityId: string) {
    return this.api.protectedFetchJson<{ snapshot: AccountSnapshot }>(`${this.base(entityId)}/sync`, {
      method: 'POST',
    })
  }

  getSmsLedger(entityId: string) {
    return this.api.protectedFetchJson<SmsCreditLedgerInfo[]>(`${this.base(entityId)}/sms-ledger`)
  }

  // --- dev-only, 404s unless the API has API_ALLOW_DEV_BILLING on and the user is root ---

  setManualTier(entityId: string, tier: PlanTier | null, note = '') {
    return this.api.protectedFetchJson<{ snapshot: AccountSnapshot }>(`${this.base(entityId)}/dev/tier`, {
      body: JSON.stringify({ tier, note }),
      method: 'POST',
    })
  }

  consumeSmsCredits(entityId: string, amount = 1) {
    return this.api.protectedFetchJson<{ balance: number; snapshot: AccountSnapshot }>(
      `${this.base(entityId)}/dev/consume-sms`,
      { body: JSON.stringify({ amount }), method: 'POST' },
    )
  }
}
